import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { Trash2, Edit, Plus, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';

interface Redirect {
  id: string;
  source_path: string;
  destination_url: string;
  redirect_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export default function Redirects() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [redirects, setRedirects] = useState<Redirect[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingRedirect, setEditingRedirect] = useState<Redirect | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    source_path: '',
    destination_url: '',
    redirect_type: '301',
    is_active: true
  });

  useEffect(() => {
    fetchRedirects();
  }, []);

  const fetchRedirects = async () => {
    try {
      const { data, error } = await supabase
        .from('redirects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRedirects(data || []);
    } catch (error) {
      console.error('Error fetching redirects:', error);
      toast({
        title: "Error",
        description: "Failed to fetch redirects",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate source path starts with /
    if (!formData.source_path.startsWith('/')) {
      toast({
        title: "Invalid source path",
        description: "Source path must start with /",
        variant: "destructive",
      });
      return;
    }

    try {
      const redirectData = {
        ...formData,
        created_by: user?.id
      };

      if (editingRedirect) {
        const { error } = await supabase
          .from('redirects')
          .update(redirectData)
          .eq('id', editingRedirect.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Redirect updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('redirects')
          .insert([redirectData]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Redirect created successfully",
        });
      }

      setFormData({
        source_path: '',
        destination_url: '',
        redirect_type: '301',
        is_active: true
      });
      setEditingRedirect(null);
      setShowForm(false);
      fetchRedirects();
    } catch (error: any) {
      console.error('Error saving redirect:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save redirect",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (redirect: Redirect) => {
    setEditingRedirect(redirect);
    setFormData({
      source_path: redirect.source_path,
      destination_url: redirect.destination_url,
      redirect_type: redirect.redirect_type,
      is_active: redirect.is_active
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('redirects')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Redirect deleted successfully",
      });
      fetchRedirects();
    } catch (error) {
      console.error('Error deleting redirect:', error);
      toast({
        title: "Error",
        description: "Failed to delete redirect",
        variant: "destructive",
      });
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('redirects')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;
      fetchRedirects();
    } catch (error) {
      console.error('Error updating redirect status:', error);
      toast({
        title: "Error",
        description: "Failed to update redirect status",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div className="animate-pulse">Loading redirects...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">URL Redirects</h1>
          <p className="text-muted-foreground">Manage 301 redirects for your website</p>
        </div>
        
        <Dialog open={showForm} onOpenChange={(open) => {
          setShowForm(open);
          if (!open) {
            setEditingRedirect(null);
            setFormData({
              source_path: '',
              destination_url: '',
              redirect_type: '301',
              is_active: true
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Redirect
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingRedirect ? 'Edit Redirect' : 'Create New Redirect'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="source_path">Source Path</Label>
                <Input
                  id="source_path"
                  value={formData.source_path}
                  onChange={(e) => setFormData(prev => ({ ...prev, source_path: e.target.value }))}
                  placeholder="/old-page"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Must start with / (e.g., /shop/)
                </p>
              </div>
              
              <div>
                <Label htmlFor="destination_url">Destination URL</Label>
                <Input
                  id="destination_url"
                  value={formData.destination_url}
                  onChange={(e) => setFormData(prev => ({ ...prev, destination_url: e.target.value }))}
                  placeholder="https://example.com/new-page"
                  required
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingRedirect ? 'Update' : 'Create'} Redirect
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Redirects ({redirects.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {redirects.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No redirects configured yet. Create your first redirect to get started.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source Path</TableHead>
                  <TableHead>Destination URL</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {redirects.map((redirect) => (
                  <TableRow key={redirect.id}>
                    <TableCell className="font-mono">
                      {redirect.source_path}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="truncate max-w-xs">{redirect.destination_url}</span>
                        <a 
                          href={redirect.destination_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">{redirect.redirect_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={redirect.is_active}
                          onCheckedChange={(checked) => toggleActive(redirect.id, checked)}
                        />
                        <Badge variant={redirect.is_active ? "default" : "secondary"}>
                          {redirect.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {new Date(redirect.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(redirect)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Redirect</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete this redirect? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(redirect.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}